import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { DegreeMasterItem } from '@/types/settings-api';

/**
 * Degree master search — PROD-761 commit 10 (Qualifications picker).
 *
 * GET /api/Master/DegreeValueSet?searchQuery={q}
 *
 * Source for the Educational → Qualifications DEGREE picker
 * (edit_medical_school_widget.dart). Uses CodeList envelope sourced
 * from HL7 v2-0360 (healthcare credential codes — RN, MSN, EMT, etc.).
 *
 * Note: the outer GUID field is **lowercase** `degree`, distinct from
 * the PascalCase `Id` used by MedicalSchools / Hospital. Do not normalise.
 *
 * Consumers map:
 *   - `DegreeMasterItem.degree`             → `EducationQualification.degree`
 *   - `DegreeMasterItem.code.displayName`   → `EducationQualification.degreeName`
 *   - `DegreeMasterItem.code`               → `EducationQualification.code`
 *
 * Wire-shape verified against live dev API 2026-05-14:
 *   [{
 *     "degree": "6316ffd9-…",
 *     "code": {
 *       "code": "RN",
 *       "displayName": "Registered Nurse",
 *       "system": "http://terminology.hl7.org/CodeSystem/v2-0360|2.7"
 *     }
 *   }]
 *
 * Caller debounces 250ms.
 */
export function useDegreeSearch(searchQuery: string) {
  const trimmed = searchQuery.trim();
  return useQuery({
    queryKey: ['settings', 'master', 'degrees', trimmed],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ searchQuery: trimmed });
      return get<DegreeMasterItem[]>(
        `/api/Master/DegreeValueSet?${qs.toString()}`,
        { signal },
      );
    },
    enabled: trimmed.length > 0,
    staleTime: 60_000,
  });
}
